import { wikiPageCollection } from "./wiki-page.collection";
import { useState } from "react";
import type { Collection } from "@tanstack/db";
import { useDbClient } from "@tanstack/react-db";
import type { WikiViewProps } from "@softmax/web-ux/wiki";
import { collectionError } from "@softmax/web-features/data";
import { communityAccess } from "@softmax/web-features/community";
import {
  wikiPermissionsCollection,
  wikiIndexCollection,
  wikiRevisionsCollection,
  type WikiIdentity,
} from "./wiki.collections";

type WikiRefreshRead = Pick<Collection, "status" | "health" | "reload">;

export function useWikiRefresh({
  scope,
  mode,
  pageId,
  permissions,
  page,
  index,
  revisions,
}: {
  scope: WikiIdentity;
  mode: WikiViewProps["mode"];
  pageId: string | undefined;
  permissions: WikiRefreshRead;
  page: WikiRefreshRead | undefined;
  index: WikiRefreshRead | undefined;
  revisions: WikiRefreshRead | undefined;
}) {
  const db = useDbClient();
  const [refreshing, setRefreshing] = useState(false);

  async function refresh(resource: Parameters<WikiViewProps["onRefresh"]>[0]) {
    const signal = db.requireDependency<AbortController>("lifetime").signal;

    if (signal.aborted) return;

    setRefreshing(true);

    try {
      const permissionsSource = db.collection(wikiPermissionsCollection(scope));
      await Promise.allSettled([
        permissions.status === "error"
          ? permissions.reload()
          : permissionsSource.utils.refetch(),
      ]);

      if (signal.aborted || collectionError(permissionsSource.health)) return;

      const pageSource = db.collection(wikiPageCollection(scope));
      const indexSource = db.collection(wikiIndexCollection(scope));
      await Promise.allSettled([
        page?.status === "error" ? page.reload() : pageSource.utils.refetch(),
        index?.status === "error"
          ? index.reload()
          : indexSource.utils.refetch(),
      ]);

      if (signal.aborted || communityAccess([pageSource.health]).accessLost)
        return;

      const currentPage = pageSource.toArray[0];

      if (!currentPage) return;

      const historyNeeded =
        resource === "history" ||
        mode === "history" ||
        revisions?.health.hasErrors;

      if (!historyNeeded) return;

      const revisionsSource = db.collection(
        wikiRevisionsCollection({ ...scope, pageId: currentPage.id }),
      );
      await Promise.allSettled([
        currentPage.id === pageId && revisions?.status === "error"
          ? revisions.reload()
          : revisionsSource.utils.refetch(),
      ]);
    } finally {
      if (!signal.aborted) setRefreshing(false);
    }
  }

  return { refreshing, refresh };
}
